import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { pedidosApi } from "page/OrderResumePag/_services/api";
import { useUserData } from "hooks/UserContext";
import { useApp } from "contexts/AppContext";
import notify from "components/UI/toast";
import {
  trackAddShippingInfoMarketplaceItems,
  trackBeginCheckoutMarketplaceItems,
} from "utils/gtm";

const channel = process.env.REACT_APP_DEFAULT_CHANNEL;

function groupByKey(array, prop) {
  return array.reduce((items, item) => {
    const key = item[prop];
    items[key] = (items[key] || []).concat(item);
    return items;
  }, {});
}

function groupItemsSeller(array) {
  if (!array) return {};
  const items = array.map((item) => ({
    ...item,
    seller: item?.seller || item?.brand_name || "Loja",
  }));
  return groupByKey(items, "seller");
}

// Normaliza a resposta de `v2/checkout/resumo`: o `cart` pode vir como objeto
// indexado por carrinho (com deliveryOptions) ou já como { items: [...] }.
function processCartData(cartData, fallbackShipping) {
  const allItems = [];
  const shippingByBrand = {};
  if (!cartData) return { allItems, shippingByBrand };

  if (Array.isArray(cartData.items)) {
    allItems.push(...cartData.items);
    if (cartData.shipping_by_brand) {
      Object.assign(shippingByBrand, cartData.shipping_by_brand);
    } else if (fallbackShipping) {
      Object.assign(shippingByBrand, fallbackShipping);
    }
    return { allItems, shippingByBrand };
  }

  Object.values(cartData).forEach((cartInfo) => {
    if (cartInfo?.items) {
      allItems.push(...cartInfo.items);
      cartInfo.items.forEach((item) => {
        const brandId = item.brand_uuid;
        if (!shippingByBrand[brandId]) {
          shippingByBrand[brandId] = {
            shipping_amount: cartInfo.deliveryOptions?.shippingPrice || 0,
            shipping_point: cartInfo.deliveryOptions?.shippingPoints || 0,
            shipping_deadline: cartInfo.deliveryOptions?.estimatedBusinessDays || 0,
            shipping_delivery_date:
              cartInfo.deliveryOptions?.estimatedDeliveryDate || null,
          };
        }
      });
    }
  });

  return { allItems, shippingByBrand };
}

export function useOrderSummary() {
  const { user, addressShipping } = useUserData();
  const { whitelabel } = useApp();

  const displayMode = whitelabel?.config?.payment_mode || "money";
  const configData = whitelabel?.config || {};
  const fatorFrete = configData[`fator_${channel}_frete`] / 10000 || 0;

  const cep =
    addressShipping?.cep ||
    addressShipping?.zipCode ||
    user?.addresses?.[0]?.cep;

  const [loading, setLoading] = useState(true);
  const [dataCheckout, setDataCheckout] = useState({
    cart: { items: [] },
    summary: {},
  });
  const [itemsBySeller, setItemsBySeller] = useState({});

  const shippingInfoOnPageSent = useRef(false);
  const beginCheckoutOnPageSent = useRef(false);

  const trackCheckoutShippingInfo = useCallback(
    (checkoutData = dataCheckout) => {
      const items = checkoutData.cart?.items || [];
      if (!items.length) return;
      const summary = checkoutData.summary || {};
      const couponCode = summary.coupon?.code || summary.coupon?.name || "";
      trackAddShippingInfoMarketplaceItems(
        items,
        checkoutData.cart?.shipping_by_brand || {},
        {
          coupon: couponCode,
          value: parseFloat(summary.total || 0),
        },
      );
    },
    [dataCheckout],
  );

  const fetchSummary = useCallback(
    async ({ silent = false } = {}) => {
      if (!user?.uuid || !cep) {
        return;
      }
      if (!silent) setLoading(true);
      try {
        const response = await pedidosApi.get(`v2/checkout/resumo/${channel}`, {
          params: { customerUuid: user.uuid, cep, fator: fatorFrete },
        });
        const checkoutData = response.data?.data || response.data || {};
        const { allItems, shippingByBrand } = processCartData(checkoutData.cart || {});
        const formatted = {
          ...checkoutData,
          cart: {
            ...(checkoutData.cart || {}),
            items: allItems,
            shipping_by_brand: shippingByBrand,
          },
        };
        setDataCheckout(formatted);
        setItemsBySeller(groupItemsSeller(allItems));

        if (!silent && allItems.length > 0) {
          const couponCode =
            formatted.summary?.coupon?.code ||
            formatted.summary?.coupon?.name ||
            "";
          const orderValue = parseFloat(formatted.summary?.total || 0);

          if (!beginCheckoutOnPageSent.current) {
            trackBeginCheckoutMarketplaceItems(allItems, {
              coupon: couponCode,
              value: orderValue,
            });
            beginCheckoutOnPageSent.current = true;
          }

          if (!shippingInfoOnPageSent.current) {
            trackAddShippingInfoMarketplaceItems(allItems, shippingByBrand, {
              coupon: couponCode,
              value: orderValue,
            });
            shippingInfoOnPageSent.current = true;
          }
        }
      } catch (e) {
        notify("Erro ao carregar o resumo da compra.", "error");
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [user?.uuid, cep, fatorFrete],
  );

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    beginCheckoutOnPageSent.current = false;
    shippingInfoOnPageSent.current = false;
  }, [cep]);

  const cartForSummary = useMemo(() => {
    const items = dataCheckout.cart?.items || [];
    const shippingByBrand = dataCheckout.cart?.shipping_by_brand || {};

    const subtotal = items.reduce((total, item) => {
      const price = parseFloat(
        item.price?.BRL?.offer || item.price?.BRL?.price || 0,
      );
      const quantity = parseInt(item.quantity || 1, 10);
      return total + price * quantity;
    }, 0);

    const shipping = Object.values(shippingByBrand).reduce(
      (total, shippingData) =>
        total + parseFloat(shippingData.shipping_amount || 0),
      0,
    );

    const total = subtotal + shipping;

    return {
      items,
      shipping_by_brand: shippingByBrand,
      summary: {
        subtotal: subtotal.toFixed(2),
        shipping: shipping.toFixed(2),
        total: total.toFixed(2),
      },
    };
  }, [dataCheckout]);

  const hasItems = (dataCheckout.cart?.items?.length || 0) > 0;

  return {
    loading,
    dataCheckout,
    itemsBySeller,
    shippingByBrand: dataCheckout.cart?.shipping_by_brand || {},
    displayMode,
    cartForSummary,
    hasItems,
    trackCheckoutShippingInfo,
  };
}
